// HomeLeaderboard.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { virtualize } from 'react-swipeable-views-utils';
import { mod } from 'react-swipeable-views-core';

import LeaderboardUserCards from '../container/LeaderboardUserCards.js';
import Tabs from '../component/Tabs.jsx';
import Tab from '../component/Tab.jsx';
import { textColor, color } from '../style/variables.js';
import TextEllipsis from '../style/TextEllipsis.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import getPublicSrcSet from '../resource/getPublicSrcSet.js';
import { PUBLIC } from '../resource/resourceUrlTypeConstants.js';
import { RANGE_24H, DEFAULT_REGION } from '../resource/leaderboardConstants.js';
import { MediaAssetFormat } from '../resource/getMediaAsset.js';

import crownIcon from '../../img/ic_crown_16.svg';
import moreIcon from '../../img/ic-arrow-right-xs.svg';

import trackMixpanel, {
  EventTypes,
  ButtonId,
  getViewId,
} from '../resource/mixpanel.js';

const HORIZONTAL_GAP = 16;

const tabsStyle = {
  paddingBottom: '2px',
};
const tabLabelStyle = {
  padding: '8px 2px',
};
const indicatorStyle = {
  borderRadius: '2px',
};
const slideStyle = {};

const VirtualizeSwipeableViews = virtualize(SwipeableViews);

export class HomeLeaderboard extends React.PureComponent {
  state = {
    region: this.props.region || DEFAULT_REGION,
  };
  timer = null;
  swipeableActions = null;
  slideRenderer = ({ key, index: sliderIndex }) => {
    const { activeCategory, isIntersecting, shouldRefresh, categories } =
      this.props;
    const { region } = this.state;
    const index = mod(sliderIndex, categories.length);
    const category = categories[index];
    const isActive = activeCategory === category;
    return (
      <LeaderboardUserCards
        key={key}
        category={category}
        region={region}
        range={RANGE_24H}
        isActive={isActive}
        isIntersecting={isIntersecting}
        updateHeight={this.swipeableActions?.updateHeight}
        shouldRefresh={shouldRefresh}
      />
    );
  };
  componentDidUpdate(prevProps) {
    const { region: prevRegion } = prevProps;
    const { region } = this.props;

    if (prevRegion !== region) {
      this.setState({
        region,
      });
    }
  }
  componentWillUnmount() {
    clearTimeout(this.timer);
  }
  trackTabOnClick = ({ category, region }) => {
    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId: `${ButtonId.Leaderboard.ButtonLeaderboardTab}`,
        extraProps: {
          'leaderboard.region': region,
          'leaderboard.tab': category,
          'leaderboard.range': RANGE_24H,
        },
      },
    });
  };
  render() {
    const { region } = this.state;
    const {
      t,
      activeCategory,
      changeCategory,
      categories,
      publicLeaderboardUrlPath,
    } = this.props;
    const hasRegion = region != null;
    const title = hasRegion
      ? t(`leaderboard_${region}`)
      : t(`leaderboard_${DEFAULT_REGION}`);
    return (
      <StyledHomeLeaderboard>
        <Header>
          <TitleSection>
            <CrownIcon src={crownIcon} width="20" height="20" />
            {title}
          </TitleSection>
          <MoreSection
            as={hasRegion ? Link : undefined}
            to={`/leaderboard/${region}/${activeCategory}/${RANGE_24H}`}
            title={`SWAG ${title}`}
          >
            {t('more')}
            <MoreIcon src={moreIcon} width="16" height="16" />
          </MoreSection>
        </Header>
        <Body>
          <TabsWrapper>
            <Tabs
              indicatorStyle={indicatorStyle}
              style={tabsStyle}
              value={activeCategory}
              onChange={(event, category) => {
                this.trackTabOnClick({ category, region, range: RANGE_24H });
                changeCategory({ category });
              }}
            >
              {categories.map(category => {
                const iconSource = getResourceUrl({
                  path: publicLeaderboardUrlPath,
                  endpoint: `/${category}.png`,
                  resourceType: PUBLIC,
                });
                const [avifSrcSet, webpSrcSet, jpgSrcSet] = [
                  MediaAssetFormat.AVIF,
                  MediaAssetFormat.WEBP,
                  MediaAssetFormat.JPG,
                ].map(format =>
                  getPublicSrcSet({ href: iconSource, size: 64, format })
                );
                return (
                  <Tab
                    key={category}
                    labelStyle={tabLabelStyle}
                    value={category}
                    label={
                      <TabLabel isActive={category === activeCategory}>
                        <TabIcon
                          aria-hidden
                          alt={`${category} icon`}
                          width="12"
                          height="12"
                        >
                          <source {...avifSrcSet} type="image/avif" />
                          <source {...webpSrcSet} type="image/webp" />
                          <img {...jpgSrcSet} type="image/jpg" />
                        </TabIcon>
                        <TabTitle>{t(`leaderboard_${category}`)}</TabTitle>
                      </TabLabel>
                    }
                  />
                );
              })}
            </Tabs>
          </TabsWrapper>
          <VirtualizeSwipeableViews
            index={categories.indexOf(activeCategory)}
            onChangeIndex={sliderIndex => {
              const index = mod(sliderIndex, categories.length);
              return changeCategory({ category: categories[index] });
            }}
            enableMouseEvents
            slideStyle={slideStyle}
            slideCount={categories.length}
            slideRenderer={this.slideRenderer}
            animateHeight
            action={actions => {
              this.swipeableActions = actions;
            }}
          ></VirtualizeSwipeableViews>
        </Body>
      </StyledHomeLeaderboard>
    );
  }
}

HomeLeaderboard.propTypes = {
  t: PropTypes.func.isRequired,
  categories: PropTypes.array,
  activeCategory: PropTypes.string,
  publicLeaderboardUrlPath: PropTypes.string,
  region: PropTypes.string,
  changeCategory: PropTypes.func,
  isIntersecting: PropTypes.bool,
  shouldRefresh: PropTypes.bool,
};

HomeLeaderboard.defaultProps = {
  activeCategory: '',
  publicLeaderboardUrlPath: '',
  categories: [],
  region: null,
  changeCategory: () => null,
  isIntersecting: false,
  shouldRefresh: false,
};

const StyledHomeLeaderboard = styled.article`
  padding: 16px 0px;
  background-color: ${color.grey[900]};
`;

const Header = styled.header`
  margin-bottom: 16px;
  padding-left: ${HORIZONTAL_GAP}px;
  padding-right: ${HORIZONTAL_GAP}px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TitleSection = styled.h2`
  display: flex;
  align-items: center;
  margin: 0px;
  color: ${textColor.white};
  font-size: 16px;
  font-weight: 600;
`;

const CrownIcon = styled.img`
  margin-right: 4px;
  border-radius: 50%;
  padding: 2px;
  background-color: ${color.yellow};
`;

const MoreSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  line-height: 1;
  color: ${textColor.white};
  font-size: 12px;
  ::after {
    position: absolute;
    content: '';
    display: block;
    top: -5px;
    bottom: -5px;
    left: 0;
    right: 0;
  }
`;

const MoreIcon = styled.img`
  margin-left: 4px;
`;

const Body = styled.section``;

const TabsWrapper = styled.div`
  position: relative;
  margin-bottom: 22px;
  padding-left: ${HORIZONTAL_GAP}px;
  padding-right: ${HORIZONTAL_GAP}px;
`;

const TabLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #ffffff;
  line-height: 1;
  ${({ isActive }) => (!isActive ? 'opacity: 0.3;' : '')}
  > picture {
    margin-right: 4px;
  }
`;

const TabIcon = styled.picture`
  flex: none;
  width: 12px;
  height: 12px;
  & > img {
    width: 100%;
    height: 100%;
  }
`;

const TabTitle = styled.h3`
  ${TextEllipsis}
  font-weight: normal;
  margin: 0;
  font-size: 12px;
`;

export default withTranslation('translation', { withRef: true })(
  HomeLeaderboard
);
